import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { CaseDetails, LeaveHoursBanksBuckets, ModelStateErrors, SelectionOption, StiiraError } from '@core/models';
import { LeaveHoursForm, LeaveHoursFormPost } from '@core/models/leave-admin/employees/leave-hours-form.model';
import { ErrorService, LayoutService } from '@core/services';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { ManageEmployeesService } from '@core/services/manage-employees.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { Subject, Observable } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { UpdateConfirmationComponent } from '../update-confirmation/update-confirmation.component';
import { UpdateConfirmationDialog } from '@core/models/shared/update-confirmation-dialog.model';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { ReplaceBankNamesPipe } from '@shared/pipes/replace-bank-name.pipe';
import { EmployeeEntitlement } from '@core/models/leave-admin/employees/employee-entitlement.model';
import { Banks } from '@core/enums';
import { EmployeeRecord } from '@core/models/leave-admin/employees/employee-record.model';
import { PendingActionsAlertComponent } from '../pending-actions-alert/pending-actions-alert.component';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';

export enum EditLeaveHoursComponentMode {
  add = -1,
  edit = -2
}

export interface EditLeaveHoursComponentData {
  mode: EditLeaveHoursComponentMode;
  eeId: number;
  caseId: number; // nullable (from case details)
  employeeEntitlement: EmployeeEntitlement; // nullable (edit mode)
  sysText: any; 
  unsavedChangesSysText: any;
  updateConfSysText: any;
  leaveHoursBanksBuckets: LeaveHoursBanksBuckets;
}

@Component({
    selector: 'app-edit-leave-hours',
    templateUrl: './edit-leave-hours.component.html',
    styleUrls: ['./edit-leave-hours.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditLeaveHoursComponent implements OnInit {
  static readonly componentName = 'EditLeaveHoursComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  public form: FormGroup<LeaveHoursForm>;
  public formInitValues: any;
  public isSubmitting$: Subject<boolean>;
  public mode = EditLeaveHoursComponentMode;
  public position = position;
  public onDragEnded = onDragEnded;
  public bankOptions: SelectionOption[] = [];
  public banks = Banks;

  private formChangeEmitted: boolean = false;
  private destroy$: Subject<void> = new Subject<void>();

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  get formIsValid(): boolean {
    return this.form.valid;
  }

  public isMobile$: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: EditLeaveHoursComponentData,
    private leaveHoursDialogRef: MatDialogRef<EditLeaveHoursComponent, EmployeeRecord | CaseDetails>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private manageEmployeesService: ManageEmployeesService,
    private leaveAdminService: LeaveAdminService,
    private leaveAdminStore: LeaveAdminStoreService,
    private errorService: ErrorService,
    private replaceBankPipe: ReplaceBankNamesPipe,
    private layoutService: LayoutService
  ) { 
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group<LeaveHoursForm>({
      effectiveDate: this.fb.control(null),
      bank: this.fb.control(null),
      bankHours: this.fb.control(null)
    })
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.leaveHoursDialogRef, this.isSubmitting$, () => this.cancel());

    this.buildBankOptions();

    if (this.data.mode == EditLeaveHoursComponentMode.edit) {
      this.form.patchValue({
        effectiveDate: this.data.employeeEntitlement.effectiveDate,
        bank: this.data.employeeEntitlement.bank,
        bankHours: this.data.employeeEntitlement.totalHours
      })
    }

    this.formInitValues = { ...this.form.value };

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.formChangeEmitted && !this.noChanges) {
          this.isEditing.emit(true);
          this.formChangeEmitted = true;
        } else if (this.noChanges) {
          this.isEditing.emit(false);
          this.formChangeEmitted = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public cancel(): void {
    if (this.noChanges) {
      this.leaveHoursDialogRef.close();
    } else {
      this.openUnsavedChangesDialog();
    }
  }
  
  public onSubmit(overrideUpdateConf: boolean = false): void {
    // from case details check if any action items running
    if (this.data.caseId && this.leaveAdminStore.isActionRunning) {
      this.openPendingChangesDialog(overrideUpdateConf);
      return;
    }

    const dto: LeaveHoursFormPost = {
      overrideUpdateConf: overrideUpdateConf,
      employeeId: this.data.eeId,
      caseId: this.data.caseId,
      employeeBankTotalHoursId: this.data.mode === EditLeaveHoursComponentMode.edit ? 
        this.data.employeeEntitlement?.employeeBankTotalHoursId : null,
      effectiveDate: this.form.controls.effectiveDate.value,
      bank: this.form.controls.bank.value,
      bankHours: this.form.controls.bankHours.value
    };

    // add
    if (this.data.mode === EditLeaveHoursComponentMode.add) {

      // employee record
      if (!this.data.caseId) {
        this.manageEmployeesService.postAddLeaveHours(dto)
          .pipe(
            indicate(this.isSubmitting$),
            finalize(() => { }))
          .subscribe((res) => {
            if (res.showUpdateConfirmationDialog) {
              this.openUpdateConfirmationDialog(res as UpdateConfirmationDialog);
            } else {
              this.leaveHoursDialogRef.close(res as EmployeeRecord);
            }
          }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, this.replaceMsErrorBankNames(err.modelStateErrors)));
        }

      //case details
      else {
        this.leaveAdminService.postAddLeaveHours(dto)
          .pipe(
            indicate(this.isSubmitting$),
            finalize(() => { }))
          .subscribe((res) => {
            if (res.showUpdateConfirmationDialog) {
              this.openUpdateConfirmationDialog(res as UpdateConfirmationDialog);
            } else {
              this.leaveHoursDialogRef.close(res as CaseDetails);
            }
          }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, this.replaceMsErrorBankNames(err.modelStateErrors)));
      }
    }

    // edit 
    else if (this.data.mode === EditLeaveHoursComponentMode.edit) {

      // employee record
      if (!this.data.caseId) {
      this.manageEmployeesService.postEditLeaveHours(dto)
        .pipe(
          indicate(this.isSubmitting$),
          finalize(() => { })
        )
        .subscribe((res) => {
          if (res.showUpdateConfirmationDialog) {
            this.openUpdateConfirmationDialog(res as UpdateConfirmationDialog);
          } else {
            this.leaveHoursDialogRef.close(res as EmployeeRecord);
          }
        }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, this.replaceMsErrorBankNames(err.modelStateErrors)));
      }

      //case details
      else {
        this.leaveAdminService.postEditLeaveHours(dto)
          .pipe(
            indicate(this.isSubmitting$),
            finalize(() => { })
          )
          .subscribe((res) => {
            if (res.showUpdateConfirmationDialog) {
              this.openUpdateConfirmationDialog(res as UpdateConfirmationDialog);
            } else {
              this.leaveHoursDialogRef.close(res as CaseDetails);
            }
          }, (err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, this.replaceMsErrorBankNames(err.modelStateErrors)));
      }
    }
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.data.unsavedChangesSysText
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.leaveHoursDialogRef.close();
        }
      });
  }

  private openUpdateConfirmationDialog(updateConfirmationDialog: UpdateConfirmationDialog): void {
    const objString = JSON.stringify(updateConfirmationDialog);
    updateConfirmationDialog = JSON
      .parse(this.replaceBankPipe
      .transform(objString, this.data.leaveHoursBanksBuckets));
    const dialogConfig: MatDialogConfig = {
      id: UpdateConfirmationComponent.componentName,
      width: "450px",
      panelClass: "mat-dialog-container-mobileWidth",
      disableClose: false,
      data: {
        sysText: this.data.updateConfSysText,
        updateConfirmationDialog: updateConfirmationDialog
      }
    };

    this.dialog.open(UpdateConfirmationComponent, dialogConfig)
      .beforeClosed().subscribe((res: boolean) => {
        if (res) {
          this.onSubmit(true);
        }
      });
  }

  private openPendingChangesDialog(overrideUpdateConf: boolean): void {
    const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px'
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
      if (res) {
        this.onSubmit(overrideUpdateConf);
      }
    });
  }

  private replaceMsErrorBankNames(msError: ModelStateErrors): ModelStateErrors {
    const msErrorString = JSON.stringify(msError);
    const msErrorReplaced: ModelStateErrors = JSON
      .parse(this.replaceBankPipe
      .transform(msErrorString, this.data.leaveHoursBanksBuckets));
    return msErrorReplaced;
  }

  private buildBankOptions(): void {
    const bankIds = Object.values(this.banks).filter(v => typeof v === 'number');
    bankIds.forEach(id => {
      // always include selected bank when editing
      if ( id === this.data.employeeEntitlement?.bank || 
        // otherwise only include banks 'in context today' (showBank*Hours)
        (id === Banks.Bank1 && this.data.leaveHoursBanksBuckets.showBank1Hours) ||
        (id === Banks.Bank2 && this.data.leaveHoursBanksBuckets.showBank2Hours) ||
        (id === Banks.Bank3 && this.data.leaveHoursBanksBuckets.showBank3Hours) ||
        (id === Banks.Bank4 && this.data.leaveHoursBanksBuckets.showBank4Hours) ||
        (id === Banks.Bank5 && this.data.leaveHoursBanksBuckets.showBank5Hours)) {
        this.bankOptions.push({
          id: id,
          description: 
            id === Banks.Bank1 ? (this.data.leaveHoursBanksBuckets.bank1Name ?? this.data.leaveHoursBanksBuckets.bank1DefaultName) :
            id === Banks.Bank2 ? (this.data.leaveHoursBanksBuckets.bank2Name ?? this.data.leaveHoursBanksBuckets.bank2DefaultName) :
            id === Banks.Bank3 ? (this.data.leaveHoursBanksBuckets.bank3Name ?? this.data.leaveHoursBanksBuckets.bank3DefaultName) :
            id === Banks.Bank4 ? (this.data.leaveHoursBanksBuckets.bank4Name ?? this.data.leaveHoursBanksBuckets.bank4DefaultName) :
            id === Banks.Bank5 ? (this.data.leaveHoursBanksBuckets.bank5Name ?? this.data.leaveHoursBanksBuckets.bank5DefaultName) : ""
        });
      }
    });
  }
}